import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, Download } from "lucide-react";
import { API } from "../config";

const DocumentsPage = () => {
  const [activeDoc, setActiveDoc] = useState('summary');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const docs = [
    { key: 'summary', label: 'Riassunto Progetto', endpoint: 'project-summary' },
    { key: 'proposal', label: 'Proposta Commerciale', endpoint: 'commercial-proposal' },
  ];

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      try {
        const doc = docs.find(d => d.key === activeDoc);
        const res = await axios.get(`${API}/docs/${doc.endpoint}`);
        setContent(res.data.content);
      } catch {
        setContent('Errore nel caricamento del documento.');
      } finally {
        setLoading(false);
      }
    };
    fetchDoc();
  }, [activeDoc]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Copiato negli appunti');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Errore nella copia'); }
  };

  const downloadDoc = () => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = docs.find(d => d.key === activeDoc).label.replace(/ /g, '_') + '.md';
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      return part;
    });
  };

  const renderMarkdown = (md) => {
    return md.split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h1 key={i} className="text-3xl font-bold text-white mt-8 mb-4">{line.slice(2)}</h1>;
      if (line.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-white mt-6 mb-3 border-b border-gray-700 pb-2">{line.slice(3)}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="text-xl font-semibold text-white mt-5 mb-2">{line.slice(4)}</h3>;
      if (line.startsWith('---')) return <hr key={i} className="border-gray-700 my-6" />;
      if (line.startsWith('- **')) {
        const match = line.match(/^- \*\*(.+?)\*\*:?\s*(.*)/);
        if (match) return <div key={i} className="flex gap-2 py-1 pl-4"><span className="text-lojack-red">-</span><span><strong className="text-white">{match[1]}</strong>{match[2] && <span className="text-gray-400">: {renderInline(match[2])}</span>}</span></div>;
      }
      if (line.startsWith('- ')) return <div key={i} className="flex gap-2 py-1 pl-4"><span className="text-lojack-red">-</span><span className="text-gray-300">{renderInline(line.slice(2))}</span></div>;
      if (line.startsWith('| ') && line.includes('|')) {
        const cells = line.split('|').filter(c => c.trim());
        if (cells.every(c => c.trim().match(/^-+$/))) return null;
        const isHeader = i > 0 && md.split('\n')[i + 1]?.includes('---');
        return (
          <div key={i} className={`grid gap-4 py-2 px-4 ${isHeader ? 'border-b border-gray-600 font-semibold text-white' : 'text-gray-300'}`} style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
            {cells.map((cell, j) => <span key={j} className="truncate">{renderInline(cell.trim())}</span>)}
          </div>
        );
      }
      if (line.startsWith('*') && line.endsWith('*') && !line.startsWith('**')) return <p key={i} className="text-gray-500 italic text-sm mt-4">{line.slice(1, -1)}</p>;
      if (line.startsWith('> ')) return <blockquote key={i} className="border-l-4 border-lojack-red pl-4 py-2 my-4 italic text-gray-300">{renderInline(line.slice(2))}</blockquote>;
      if (line.trim() === '') return <div key={i} className="h-2" />;
      return <p key={i} className="text-gray-300 leading-relaxed">{renderInline(line)}</p>;
    });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto animate-fade-in" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Documenti</h1>
          <p className="text-gray-400 mt-1">Riassunto progetto e proposta commerciale</p>
        </div>
        <div className="flex gap-2">
          <button onClick={copyToClipboard} className="btn-lojack-outline flex items-center gap-2 text-sm" data-testid="copy-doc-btn">
            {copied ? <Check size={16} /> : <Copy size={16} />}{copied ? 'Copiato!' : 'Copia testo'}
          </button>
          <button onClick={downloadDoc} className="btn-lojack flex items-center gap-2 text-sm" data-testid="download-doc-btn">
            <Download size={16} />Scarica .md
          </button>
        </div>
      </div>
      <div className="flex gap-2 mb-6">
        {docs.map(doc => (
          <button key={doc.key} onClick={() => setActiveDoc(doc.key)}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeDoc === doc.key ? 'bg-lojack-red text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
            data-testid={`tab-${doc.key}`}>{doc.label}</button>
        ))}
      </div>
      <div className="wallet-card">
        {loading ? <div className="flex items-center justify-center py-12"><div className="spinner"></div></div>
        : <div className="prose-dark" data-testid="doc-content">{renderMarkdown(content)}</div>}
      </div>
    </div>
  );
};

export default DocumentsPage;
